import env from '../config/env';
import { getLoadedPermissions } from '../context/PermissionContext';

/**
 * RBAC — il gating del frontend, su permessi.
 *
 * Un solo predicato, `can(codice)`, valutato sui permessi che il backend ha dichiarato
 * per l'utente corrente (`GET /v1/me/permissions`). Sostituisce i sette predicati sui
 * ruoli del modello di Fase 2 e il loro ramo legacy: il resolver del backend risolve i
 * permessi dal `sub` e non dalla forma del token, quindi la risposta è corretta anche per
 * un utente su realm non migrato e non c'è più niente da compensare lato frontend.
 *
 * Chiude §2.2 per le rotte: prima di questo modulo il frontend deduceva i permessi, e
 * nessun test collegava le sue deduzioni alla matrice del backend. Ora li legge, e
 * `FrontendContractTest` impone che le 8 rotte di `ROUTE_PERMISSIONS` chiedano un
 * sottoinsieme di ciò che il backend concede. Non copre i permessi passati a `can()` dai
 * pulsanti e dalle voci di menù: quel gating a livello di azione resta verificato a mano.
 *
 * Se l'autenticazione Keycloak è disabilitata (`!env.useKeycloakAuth`) tutti i check
 * tornano true: è l'accesso libero in locale, invariato da Fase 2.
 */

/** Un codice permesso è `risorsa.azione`: qualunque altra forma è un errore di chiamata. */
const isPermissionCode = (code) => typeof code === 'string' && code.includes('.');

class RoleService {
  /** True se l'utente corrente ha il permesso indicato. */
  can(code) {
    if (!env.useKeycloakAuth) {
      return true;
    }
    if (!isPermissionCode(code)) {
      // Non si concede su un codice malformato: un refuso diventerebbe un pulsante
      // sempre visibile che dà 403, che è precisamente la divergenza da chiudere.
      console.warn(`RoleService.can: codice permesso non valido: ${code}`);
      return false;
    }
    return getLoadedPermissions().has(code);
  }

  /** True se l'utente ha almeno uno dei permessi indicati. */
  canAny(codes) {
    if (!env.useKeycloakAuth) {
      return true;
    }
    return Array.isArray(codes) && codes.some((code) => this.can(code));
  }
}

export default new RoleService();
